import { useEffect, useState } from "react";
import type { RefObject } from "react";

export type WatermarkTileMetrics = {
  width: number;
  height: number;
};

const MIN_TILE_SIZE = 1;

const resolveScrollContainer = (docRoot: HTMLElement): HTMLElement => {
  const contentScroll = docRoot.closest<HTMLElement>(
    '[data-content-scroll="true"]'
  );
  if (contentScroll) {
    return contentScroll;
  }

  const workspaceContainer = docRoot.closest<HTMLElement>(
    ".workspace-doc-container"
  );
  if (workspaceContainer) {
    return workspaceContainer;
  }

  let parent = docRoot.parentElement;
  while (parent) {
    const style = window.getComputedStyle(parent);
    const scrollableY =
      style.overflowY === "auto" ||
      style.overflowY === "scroll" ||
      style.overflow === "auto" ||
      style.overflow === "scroll";
    if (scrollableY && parent.clientHeight > 0) {
      return parent;
    }
    parent = parent.parentElement;
  }

  return docRoot;
};

const readAppBarHeight = (): number => {
  const raw = getComputedStyle(document.documentElement).getPropertyValue(
    "--appbar-height"
  );
  const parsed = Number.parseFloat(raw);
  return Number.isFinite(parsed) ? parsed : 48;
};

const measureTileMetrics = (
  docRoot: HTMLElement | null
): WatermarkTileMetrics | null => {
  if (!docRoot) {
    return null;
  }

  const width = docRoot.clientWidth;
  if (width <= 0) {
    return null;
  }

  const scrollContainer = resolveScrollContainer(docRoot);
  const containerHeight = scrollContainer.clientHeight;
  const fallbackHeight = Math.max(
    window.innerHeight - readAppBarHeight(),
    MIN_TILE_SIZE
  );
  const viewportHeight = containerHeight > 0 ? containerHeight : fallbackHeight;
  // A document shorter than the viewport gets its label centred on the
  // document, not on a viewport-tall tile whose middle lies past its end.
  const docHeight = docRoot.offsetHeight;
  const height =
    docHeight > 0 ? Math.min(viewportHeight, docHeight) : viewportHeight;

  return {
    width: Math.max(width, MIN_TILE_SIZE),
    height: Math.max(height, MIN_TILE_SIZE),
  };
};

/**
 * Tracks doc-root width and scroll-viewport height (capped at the document's
 * own height) for watermark tile sizing.
 */
export const useWatermarkTileMetrics = (
  docRootRef: RefObject<HTMLElement | null>
): WatermarkTileMetrics | null => {
  const [metrics, setMetrics] = useState<WatermarkTileMetrics | null>(null);

  useEffect(() => {
    const docRoot = docRootRef.current;
    if (!docRoot) {
      return;
    }

    const updateMetrics = () => {
      const next = measureTileMetrics(docRootRef.current);
      setMetrics((prev) => {
        if (
          prev &&
          next &&
          prev.width === next.width &&
          prev.height === next.height
        ) {
          return prev;
        }
        return next;
      });
    };

    updateMetrics();

    const resizeObserver = new ResizeObserver(() => {
      updateMetrics();
    });

    resizeObserver.observe(docRoot);

    const scrollContainer = resolveScrollContainer(docRoot);
    if (scrollContainer !== docRoot) {
      resizeObserver.observe(scrollContainer);
    }

    window.addEventListener("resize", updateMetrics);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateMetrics);
    };
  }, [docRootRef]);

  return metrics;
};
